// Dimension picker — DS TwoPaneMenu in a click-outside popover.
//
// Left pane = dimension categories (Geography, Channel, Origin, Tracking),
// right pane = the dimensions under the active category. Used for each of the
// deepdive table's dimensions; the optional ones pass `allowNone` so they can be
// cleared (no drill-down).
//
// The popover is PORTALED INTO the nearest scroll container and positioned
// `absolute` in that container's content coordinates, so the browser scrolls it
// natively in lockstep with the trigger — no JS-on-scroll re-anchoring, which
// trails the compositor by a frame and makes the menu visibly "jiggle". It still
// escapes the report card's `overflow-hidden` (the scroller is an ancestor of the
// card) and is clipped by the scroller instead of riding over sticky nav. The
// TwoPaneMenu's own panes scroll internally. No search field (showSearch=false)
// — the list is short — and the menu's white root is forced transparent so only
// the bordered/rounded inner panel shows.
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { usePrefersReducedMotion } from './customize-reveal'
import {
	AppButton,
	ListItem,
	TwoPaneMenu,
	TwoPaneMenuItem
} from '@clickguard/design-system'
import { ChevronDown, Rows03 } from './icons'
import { useDimensionCatalog } from '../data/use-analytics-data'
import { DimensionId } from '../types'
import { getScrollParent } from './scroll-parent'
import { acquirePositioned, releasePositioned } from 'components/data-table-v2'
import './dimension-picker.css'

// 'status' (Bad/Suspicious) is dropped from the deep-dive table's grouping picker
// — it's redundant as a table dimension (Gustavo). It stays available to the
// charts (the veracity pie is "Bad clicks by Status"), which build their own
// dimension list from DIMENSION_CATEGORIES, so only this table picker is filtered.
const TABLE_HIDDEN_DIMENSIONS = new Set(['status'])

export interface DimensionPickerProps {
	value: DimensionId | null
	onChange: (id: DimensionId | null) => void
	/** Allow clearing the selection (for the optional dimensions). */
	allowNone?: boolean
	/** Leading icon for the trigger. */
	icon?: React.ReactNode
	/** Empty-state prompt for an additive slot. Numbered per slot ("Add dimension 2")
	 *  so multiple empty pickers don't read as a repeated/buggy "Add dimension". */
	addLabel?: string
	/** Dimensions already used by the sibling pickers — hidden here so the same
	 *  dimension can't be grouped by twice (Country > Country renders nonsense). */
	exclude?: (DimensionId | null)[]
	/** Background-warm a dimension's data. Called for each dimension in the category
	 *  the user is browsing (on open + on category change) so picking it is instant.
	 *  Wired only for the primary (top-level) picker. */
	onPrefetch?: (dimensionId: DimensionId) => void
}

export const DimensionPicker = ({
	value,
	onChange,
	allowNone = false,
	icon,
	addLabel,
	exclude,
	onPrefetch
}: DimensionPickerProps) => {
	const reduced = usePrefersReducedMotion()
	const [open, setOpen] = useState(false)
	const triggerRef = useRef<HTMLDivElement>(null)
	const popRef = useRef<HTMLDivElement>(null)
	const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
	// The scroll container we portal into (it must be positioned to anchor the
	// absolute menu — see the lease below).
	const scRef = useRef<HTMLElement | null>(null)
	// The container is shared with the other anchored popovers (icon-menu, the
	// schedule drawer, the kit's AnchoredPopover), so its `position` goes through
	// the ref-counted lease rather than a private save/restore — otherwise this
	// menu closing would un-anchor another one still open on the same container.
	const heldRef = useRef(false)
	const openRef = useRef(open)
	openRef.current = open
	const acquireLease = (sc: HTMLElement) => {
		if (heldRef.current) return
		acquirePositioned(sc)
		heldRef.current = true
	}
	const releaseLease = () => {
		if (!heldRef.current) return
		if (scRef.current) releasePositioned(scRef.current)
		heldRef.current = false
	}
	// Unmounting while open (route change) must not leak the lease.
	useEffect(() => releaseLease, [])

	const place = () => {
		const trigger = triggerRef.current
		const sc = scRef.current
		if (!trigger || !sc) return
		const tr = trigger.getBoundingClientRect()
		const sr = sc.getBoundingClientRect()
		// Trigger position in the container's scrollable content space, so the
		// absolute menu rides the content as it scrolls (no JS-on-scroll needed).
		setPos({
			top: Math.round(tr.bottom - sr.top + sc.scrollTop + 6),
			left: Math.round(Math.max(8, tr.left - sr.left + sc.scrollLeft))
		})
	}

	const toggle = () => {
		if (open) {
			setOpen(false)
			return
		}
		const sc = getScrollParent(
			triggerRef.current,
			document.scrollingElement as HTMLElement
		)
		scRef.current = sc
		// Make the container a containing block for the absolute menu (leased; the
		// release waits for the exit animation, see onExitComplete).
		acquireLease(sc)
		place()
		setOpen(true)
	}

	useEffect(() => {
		if (!open) return
		const onDoc = (e: MouseEvent) => {
			const t = e.target as Node
			if (!triggerRef.current?.contains(t) && !popRef.current?.contains(t))
				setOpen(false)
		}
		const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
		// No scroll handler — the menu is in the scroll container's content and
		// moves with it natively (that's what kills the jiggle). Resize only.
		const onResize = () => place()
		document.addEventListener('mousedown', onDoc)
		document.addEventListener('keydown', onEsc)
		window.addEventListener('resize', onResize)
		return () => {
			document.removeEventListener('mousedown', onDoc)
			document.removeEventListener('keydown', onEsc)
			window.removeEventListener('resize', onResize)
		}
	}, [open])

	// Server-driven dimension list (labels + availability) with the static
	// registry as its synchronous fallback.
	const { categories: catalog, labelFor } = useDimensionCatalog()

	// Hide dimensions the sibling pickers already hold (own value stays listed)
	// and the ones this picker never offers; drop a category once it's empty.
	const taken = new Set(
		(exclude ?? []).filter((d): d is DimensionId => !!d && d !== value)
	)
	const categories = catalog
		.map((c) => ({
			id: c.id,
			label: c.label,
			items: c.dimensions
				.filter((d) => !TABLE_HIDDEN_DIMENSIONS.has(d.id) && !taken.has(d.id))
				.map((d) => ({ id: `${c.id}:${d.id}`, label: d.label }))
		}))
		.filter((c) => c.items.length > 0)
	const activeCat = value
		? categories.find((c) => c.items.some((i) => i.id.endsWith(`:${value}`)))
		: undefined
	const selectedItemId = activeCat?.items.find((i) =>
		i.id.endsWith(`:${value}`)
	)?.id

	// Warm every dimension in a category so clicking one is instant. Fired for the
	// category the menu opens to, and again whenever the user browses to another.
	const prefetchCategory = (catId?: string) => {
		if (!onPrefetch || !catId) return
		categories
			.find((c) => c.id === catId)
			?.items.forEach((i) => onPrefetch(i.id.split(':')[1] as DimensionId))
	}
	useEffect(() => {
		if (open) prefetchCategory(activeCat?.id ?? categories[0]?.id)
		// Only when the menu opens — categories/activeCat are read at fire time.
	}, [open])

	const handleSelect = (item: TwoPaneMenuItem) => {
		onChange(item.id.split(':')[1] as DimensionId)
		setOpen(false)
	}

	// Empty + clearable = an additive drill-down slot, so prompt "Add dimension"
	// (reads as an affordance) rather than the ambiguous "None". The required
	// primary picker still falls back to "Select dimension".
	const triggerLabel = value
		? labelFor(value)
		: allowNone
		? addLabel ?? 'Add dimension'
		: 'Select dimension'

	return (
		<div ref={triggerRef} className="relative inline-block">
			<AppButton
				variant="secondary"
				size="sm"
				label={triggerLabel}
				leftIcon={icon ?? <Rows03 className="w-4 h-4" />}
				rightIcon={<ChevronDown className="w-4 h-4" />}
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={toggle}
			/>
			{pos &&
				scRef.current &&
				createPortal(
					<AnimatePresence
						// Release the lease only after the exit animation: restoring the
						// container mid-exit swaps the menu's containing block and it jumps.
						// Guarded by openRef so a reopen during the exit keeps the lease.
						onExitComplete={() => {
							if (!openRef.current) releaseLease()
						}}
					>
						{open && (
							<motion.div
								ref={popRef}
								// Pane height is floored in dimension-picker.css (min-height on the
								// listboxes) so the panes extend below the 4 categories, giving the
								// pinned Clear its own space. A broad `[&>div>div]` min-height here
								// also hit the Clear container's divider div (ballooning a 1px line
								// to 212px and hiding the categories) — so it lives in the CSS now.
								className="pa-dim-picker relative z-[60] w-[440px]"
								style={{
									position: 'absolute',
									top: pos.top,
									left: pos.left,
									transformOrigin: 'top left'
								}}
								// Pop from trigger: subtle fade + 3% scale + small downward slide (standardized
								// with Filters/Columns). Snappy ease-out-expo.
								initial={
									reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: -6 }
								}
								animate={
									reduced ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }
								}
								exit={
									reduced ? { opacity: 0 } : { opacity: 0, scale: 0.985, y: -4 }
								}
								transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
							>
								<TwoPaneMenu
									// DS 0.18: twoTone paints the canonical card (panes carry their
									// own bg/padding/rounding, built-in seam divider, hairline ring)
									// — replaces the dimension-picker.css overrides that restyled
									// 0.16 internals. minPaneHeight floors the panes so the pinned
									// Clear row keeps its own space (was a CSS min-height; 0.18's
									// flex-1 panes still honor min-height).
									twoTone
									categories={categories}
									showSearch={false}
									defaultSelectedCategoryId={activeCat?.id ?? categories[0]?.id}
									selectedItemId={selectedItemId}
									onSelectItem={handleSelect}
									onSelectCategory={prefetchCategory}
									maxPaneHeight={260}
									minPaneHeight={210}
								/>
								{/* Clear pinned to the bottom of the left (categories) pane, styled to
						    match the report-title popover's Cancel row (canvas-header.tsx): a
						    DS ListItem with a padding-inset divider above it. The container is
						    transparent so the gray pane — including its rounded bottom-left
						    corner — shows through; the ListItem's own padding keeps its hover
						    bg clear of the card edge. The left pane is 2/5 of the panel. */}
								{allowNone && (
									<div className="absolute bottom-0 left-0 w-2/5 p-2">
										<div className="mx-2 my-1 h-px bg-[rgb(var(--cg-border))]" />
										<ListItem
											label={
												<span className="font-medium text-[rgb(var(--cg-brand))]">
													Clear
												</span>
											}
											onClick={() => {
												onChange(null)
												setOpen(false)
											}}
										/>
									</div>
								)}
							</motion.div>
						)}
					</AnimatePresence>,
					scRef.current
				)}
		</div>
	)
}
